
export const CURRENT_VERSION = '1.2.0';

export const APP_VERSIONS = [
  {
    version: '1.0.0',
    features: ['Initial release', 'Basic feed functionality'],
  },
  {
    version: '1.1.0',
    features: ['Profile page', 'Post creation'],
  },
   {
    version: '1.2.0',
    features: ['Trends Page', 'Discover Page', 'tAG Teams'],
  },
];

// Emergency kill-switch for Media V2 - allows instant rollback to stable player
export const USE_MEDIA_V2 = import.meta.env.VITE_MEDIA_V2 === '1';

// Helper function to determine effective dev mode - MUST be defined before FEATURE_FLAGS
function getEffectiveDevMode(): boolean {
  // In actual production, always return false
  if (!import.meta.env.DEV) return false;
  
  // In development, check for override
  const devModeOverride = localStorage.getItem('devModeOverride');
  if (devModeOverride !== null) {
    return JSON.parse(devModeOverride);
  }
  
  // Default to true in development
  return true;
}

export const FEATURE_FLAGS = {
  TRENDS_PAGE: {
    key: 'TRENDS_PAGE',
    name: 'Trends Page',
    description: 'Enable the Trends page in the main navigation',
    enabled: true,
    version: '1.2.0',
  },
  DISCOVER_PAGE: {
    key: 'DISCOVER_PAGE',
    name: 'Discover Page',
    description: 'Enable the Discover page in the main navigation',
    enabled: true,
    version: '1.2.0',
  },
  TAG_TEAMS: {
    key: 'TAG_TEAMS',
    name: 'tAG Teams',
    description: 'Enable the tAG Teams page in the main navigation',
    enabled: true,
    version: '1.2.0',
  },
  ADMIN_PAGE: {
    key: 'ADMIN_PAGE',
    name: 'Admin Page',
    description: 'Enable the Admin page in the main navigation - DEVELOPMENT ONLY',
    enabled: getEffectiveDevMode(), // Use the new function
    version: '1.0.0',
    environment: 'development' as const,
  },
  USDA_INTEGRATION: {
    key: 'USDA_INTEGRATION',
    name: 'USDA Integration',
    description: 'Enable the USDA Integration and API calls - DEVELOPMENT ONLY',
    enabled: false, // Disabled - feature removed
    version: '1.0.0',
    environment: 'development' as const,
  },
  ANALYTICS_PAGE: {
    key: 'ANALYTICS_PAGE',
    name: 'Analytics Page',
    description: 'Enable the Analytics page access - DEVELOPMENT ONLY',
    enabled: getEffectiveDevMode(), // Use the new function
    version: '1.0.0',
    environment: 'development' as const,
  },
  LANGUAGE_TRANSLATION: {
    key: 'LANGUAGE_TRANSLATION',
    name: 'Language Translation',
    description: 'Multi-language support with i18next',
    enabled: true,
    version: '1.3.0',
    environment: 'development' as const,
  },
  SOCIAL_SHARING: {
    key: 'SOCIAL_SHARING',
    name: 'Social Sharing',
    description: 'Enable social media sharing functionality',
    enabled: true,
    version: '1.2.0',
  },
  SOCIAL_ACCOUNTS_MANAGER: {
    key: 'SOCIAL_ACCOUNTS_MANAGER',
    name: 'Social Accounts Manager',
    description: 'Manage connected social media accounts',
    enabled: true,
    version: '1.2.0',
  },
  SOCIAL_LOGIN: {
    key: 'SOCIAL_LOGIN',
    name: 'Social Login',
    description: 'Enable social media login buttons (Facebook, Twitter, etc.)',
    enabled: true,
    version: '1.2.0',
  },
  CONNECTION_AWARE_EXPERIENCE: {
    key: 'CONNECTION_AWARE_EXPERIENCE',
    name: 'Connection-Aware Experience',
    description: 'Enable comprehensive connection-aware optimizations and adaptive modes',
    enabled: true,
    version: '1.5.0',
  },
  FULL_MODE: {
    key: 'FULL_MODE',
    name: 'Full Mode',
    description: 'High-quality experience for fast connections (Fiber, Starlink)',
    enabled: true,
    version: '1.5.0',
  },
  BALANCED_MODE: {
    key: 'BALANCED_MODE',
    name: 'Balanced Mode',
    description: 'Optimized experience for moderate connections (4G rural)',
    enabled: true,
    version: '1.5.0',
  },
  LITE_MODE: {
    key: 'LITE_MODE',
    name: 'Lite Mode',
    description: 'Optimized experience for slow connections (3G/2G)',
    enabled: true,
    version: '1.5.0',
  },
  OFFLINE_MODE: {
    key: 'OFFLINE_MODE',
    name: 'Offline Mode',
    description: 'Cached content experience when no network is available',
    enabled: true,
    version: '1.5.0',
  },
  LITE_MODE_AUTO_DETECT: {
    key: 'LITE_MODE_AUTO_DETECT', 
    name: 'Lite Mode Auto-Detection',
    description: 'Automatically detect slow connections and enable lite mode',
    enabled: true,
    version: '1.5.0',
  },
  LITE_MODE_TOGGLE: {
    key: 'LITE_MODE_TOGGLE',
    name: 'Lite Mode Toggle',
    description: 'Show lite mode toggle controls in UI',
    enabled: true,
    version: '1.5.0',
  },
  LITE_MODE_BADGE: {
    key: 'LITE_MODE_BADGE',
    name: 'Lite Mode Status Badge',
    description: 'Display connection status and lite mode badge',
    enabled: true,
    version: '1.5.0',
  },
  PERFORMANCE_MONITOR: {
    key: 'PERFORMANCE_MONITOR',
    name: 'Performance Monitor',
    description: 'Monitor and log performance metrics for optimization',
    enabled: getEffectiveDevMode(),
    version: '1.5.0',
    environment: 'development' as const,
  },
  ADAPTIVE_IMAGE_QUALITY: {
    key: 'ADAPTIVE_IMAGE_QUALITY',
    name: 'Adaptive Image Quality',
    description: 'Automatically adjust image quality based on connection speed',
    enabled: true,
    version: '1.5.0',
  },
  REDUCED_ANIMATIONS: {
    key: 'REDUCED_ANIMATIONS',
    name: 'Reduced Animations',
    description: 'Reduce animations and transitions for better performance on slow devices',
    enabled: true,
    version: '1.5.0',
  },
  OFFLINE_CACHE: {
    key: 'OFFLINE_CACHE',
    name: 'Offline Cache',
    description: 'Cache content for offline viewing during connectivity issues',
    enabled: false,
    version: '1.5.0',
  },
  STRICT_CSP_FOR_LITE_MODE: {
    key: 'STRICT_CSP_FOR_LITE_MODE',
    name: 'Strict CSP for Lite Mode',
    description: 'Apply stricter Content Security Policy when in lite or offline mode to reduce network requests',
    enabled: false, // Temporarily disabled - causing CSP violations blocking fonts and scripts
    version: '1.5.0',
  },
  LOGIN_REQUIRED: {
    key: 'LOGIN_REQUIRED',
    name: 'Login Required',
    description: 'Require users to login before accessing the app',
    enabled: false,
    version: '1.0.0',
  },
  USER_TRUST_LAYER: {
    key: 'USER_TRUST_LAYER',
    name: 'User Trust Layer',
    description: 'Enable user trust and reputation tracking system',
    enabled: false,
    version: '1.3.0',
  },
  LEFT_CONSOLE: {
    key: 'LEFT_CONSOLE',
    name: 'Left Activity Console',
    description: 'Enable the left sidebar with activity, suggestions, notifications, and messages',
    enabled: import.meta.env.DEV ? true : false,
    version: '1.6.0',
    environment: 'development' as const,
  },
} as const;

type FeatureKey = keyof typeof FEATURE_FLAGS;

// Export the helper function for use in components
export const isInDevMode = (): boolean => {
  return getEffectiveDevMode();
};

export const isFeatureEnabled = (featureKey: FeatureKey): boolean => {
  const localStorageFlags = localStorage.getItem('featureFlags');
  if (localStorageFlags) {
    const flags = JSON.parse(localStorageFlags);
    if (flags.hasOwnProperty(featureKey)) {
      return flags[featureKey];
    }
  }
  return FEATURE_FLAGS[featureKey].enabled;
};

export const isTabEnabled = (tabId: string): boolean => {
  switch (tabId) {
    case 'trends':
      return isFeatureEnabled('TRENDS_PAGE');
    case 'discover':
      return isFeatureEnabled('DISCOVER_PAGE');
    case 'tag-teams':
      return isFeatureEnabled('TAG_TEAMS');
    case 'admin':
      return isFeatureEnabled('ADMIN_PAGE');
    case 'usda-integration':
      return isFeatureEnabled('USDA_INTEGRATION');
    default:
      return true;
  }
};

export const getFeatureFlagStatus = () => {
  return {
    environment: import.meta.env.MODE,
    currentVersion: CURRENT_VERSION,
  };
};

export const getCurrentVersionFeatures = () => {
  return APP_VERSIONS.find(v => v.version === CURRENT_VERSION)?.features || [];
};
